// Modules
import type {GetStaticProps} from 'next';
import {useTranslation} from '@app/hooks/use-translation';
import {serverSideTranslations} from '@app/modules/locale-management/server';
import {LandingSections} from '@tempo/landing';
import {
	DevicesSection,
	FeaturesHero,
	ShakerSection,
	FlowSection,
	LyricsSection,
	OfflineSection,
	PersonalizedReco,
	SongCatcherSection,
	TransferSection,
	AiLabelSection,
	MdmMdySection,
	CustoSection,
	UniversalSharingSection,
	ACPSSection,
	PodcastsSection,
	PurpleClubSection,
	MusicQuizSection,
} from '@app/views/features/home';
import {HifiSection} from '@app/views/features/home/hifi';
import {TempoLandingPage} from '@app/components/layout/LandingPage';
import type {LandingProps} from '@app/types/next';
import {getFooterLinksPerLang} from '@app/modules/ssg-data/unlogged-data';
import {useUser} from '@deezer/react-user';

const HomePage: React.FC<LandingProps> = () => {
	const {t} = useTranslation({ns: ['features']});
	const pageTitle = t('featureslandingpage_text_seotitle_standalone-web');
	const pageDescription = t(
		'featureslandingpage_text_seodescription_standalone-web',
	);
	const [user] = useUser();
	return (
		<TempoLandingPage title={pageTitle} description={pageDescription}>
			<LandingSections topGutter>
				<FeaturesHero />
				<AiLabelSection />
				<ACPSSection />
				<CustoSection />
				<MdmMdySection />
				<UniversalSharingSection />
				<PersonalizedReco />
				<ShakerSection />
				<FlowSection />
				<TransferSection />
				<HifiSection />
				<LyricsSection />
				<OfflineSection />
				<SongCatcherSection />
				<DevicesSection />
				<PodcastsSection />
				<MusicQuizSection />
				{user?.country === 'FR' && <PurpleClubSection />}
			</LandingSections>
		</TempoLandingPage>
	);
};

export const getStaticProps: GetStaticProps<LandingProps> = async ({
	locale = '',
	locales = [],
}) => {
	const translations = await serverSideTranslations(locale, [
		'features',
		'flow',
		'musicquiz',
		'offline',
		'purpleclub',
		'common',
	]);
	return {
		props: {
			...translations,
			dehydratedState: {
				...(await getFooterLinksPerLang({locale, locales})),
			},
			tracking: {
				pagecategory: 'features',
				pagename: 'features',
				cdpViewId: 'features',
			},
		},
		revalidate: 15 * 60,
	};
};

export default HomePage;
